import { usePageTitle } from '../../hooks/usePageTitle';
import GuideSidebar from '../../components/guides/GuideSidebar';
import GuideAccordion from '../../components/guides/GuideAccordion';
import ComparisonTable from '../../components/guides/ComparisonTable';
import { Download, Search } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Link } from 'react-router-dom';

export default function YachtEssentials() {
  usePageTitle('Yacht Essentials: From Basics to Beyond');

  return (
    <div className="min-h-screen pt-32 px-6 md:px-12 lg:px-24 max-w-[1600px] mx-auto pb-24">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <GuideSidebar />
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3">
          {/* Header */}
          <div className="mb-12">
            <p className="text-xs uppercase tracking-[0.2em] text-gold mb-4">Complete Knowledge Hub</p>
            <h1
              className="text-4xl md:text-5xl font-medium tracking-tight mb-6"
              style={{ fontFamily: 'Playfair Display, serif' }}
              data-testid="essentials-title"
            >
              Yacht Essentials: From Basics to Beyond
            </h1>
            <p className="text-lg text-white/70 leading-relaxed mb-8">
              Your comprehensive A-to-Z guide to understanding yachts. Perfect for new yacht enthusiasts in Phuket, Southeast Asia, and beyond. Learn side-by-side with us as we explore every aspect of luxury yachting.
            </p>

            {/* Search & Download */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={18} />
                <Input
                  placeholder="Search guides..."
                  className="pl-10 bg-transparent border-white/20 text-white"
                  data-testid="guide-search"
                />
              </div>
              <Button className="bg-gold text-[#0A192F] hover:bg-[#E5C55D]" data-testid="download-guide-btn">
                <Download size={18} className="mr-2" />
                Download Full Guide (PDF)
              </Button>
            </div>
          </div>

          {/* Content Sections */}
          <div className="space-y-2">
            <GuideAccordion title="What is a Yacht?" defaultOpen={true}>
              <p>
                A <strong>yacht</strong> is a recreational watercraft designed for pleasure cruising, racing, or luxury living. While there's no universally agreed definition, yachts are typically characterized by their size (over 30 feet/10 meters), quality of construction, and amenities that prioritize comfort and style.
              </p>
              <div className="bg-white/5 p-6 my-4 border-l-2 border-gold">
                <p className="text-white/90">
                  <strong>Key Distinction:</strong> Unlike basic boats used for fishing or transport, yachts are purpose-built for leisure, often featuring luxurious cabins, modern entertainment systems, and professional-grade navigation equipment.
                </p>
              </div>
              <p>
                <strong>Origin:</strong> The word "yacht" comes from the Dutch word <em>jacht</em>, meaning "hunt" or "chase," originally referring to fast, light vessels used by the Dutch navy in the 17th century.
              </p>
            </GuideAccordion>

            <GuideAccordion title="Yacht Size Categories">
              <ComparisonTable
                headers={['Category', 'Length (ft)', 'Length (m)', 'Typical Features', 'Crew']}
                rows={[
                  ['Small/Day Yachts', '30-50 ft', '10-15 m', 'Day cruising, basic amenities', 'Owner-operated'],
                  ['Mid-Size Yachts', '50-80 ft', '15-24 m', '2-3 cabins, galley, entertainment', '0-2 crew'],
                  ['Large Yachts', '80-120 ft', '24-36 m', '4-5 cabins, professional systems', '2-4 crew'],
                  ['Superyachts', '120-200 ft', '36-60 m', 'Multiple decks, luxury amenities, tender', '5-12+ crew'],
                  ['Megayachts', '200+ ft', '60+ m', 'Hotel-level luxury, pools, helipads', '20-50+ crew'],
                ]}
              />
              <p className="mt-4">
                <strong>Note:</strong> In Southeast Asia, most charter yachts fall into the 50-120 ft range, perfect for exploring the Andaman Sea, Raja Ampat, and island archipelagos with shallow draft requirements.
              </p>
            </GuideAccordion>

            <GuideAccordion title="Motor vs. Sailing Yachts">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="glass-light p-6">
                  <h4 className="text-gold font-semibold mb-3 text-lg">Motor Yachts (Power)</h4>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="text-gold mt-1">•</span>
                      <span><strong>Speed:</strong> 15-30+ knots (faster travel)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gold mt-1">•</span>
                      <span><strong>Space:</strong> More interior volume, multiple decks</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gold mt-1">•</span>
                      <span><strong>Comfort:</strong> Stabilizers reduce rolling</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gold mt-1">•</span>
                      <span><strong>Cost:</strong> Higher fuel consumption</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gold mt-1">•</span>
                      <span><strong>Best for:</strong> Short charters, island hopping, luxury</span>
                    </li>
                  </ul>
                </div>

                <div className="glass-light p-6">
                  <h4 className="text-gold font-semibold mb-3 text-lg">Sailing Yachts</h4>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="text-gold mt-1">•</span>
                      <span><strong>Speed:</strong> 6-12 knots (wind-dependent)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gold mt-1">•</span>
                      <span><strong>Space:</strong> Less interior, but often wider beam</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gold mt-1">•</span>
                      <span><strong>Experience:</strong> Authentic sailing, eco-friendly</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gold mt-1">•</span>
                      <span><strong>Cost:</strong> Lower fuel costs (wind-powered)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gold mt-1">•</span>
                      <span><strong>Best for:</strong> Long passages, traditional yachting</span>
                    </li>
                  </ul>
                </div>
              </div>
            </GuideAccordion>

            <GuideAccordion title="Catamaran vs. Monohull">
              <p>
                One of the most important decisions in yachting, especially in Southeast Asian waters with shallow lagoons and coral reefs.
              </p>
              <ComparisonTable
                headers={['Feature', 'Catamaran', 'Monohull']}
                rows={[
                  ['Draft (depth)', '4-5 ft (shallow)', '8-10 ft (deep)'],
                  ['Stability', 'Very stable, minimal rolling', 'More rolling in waves'],
                  ['Speed (sailing)', 'Faster in light winds', 'Better in heavy winds'],
                  ['Interior Space', 'Huge saloon, wide beam', 'Narrower but deeper'],
                  ['Anchoring', 'Excellent for lagoons', 'Better in deep harbors'],
                  ['Southeast Asia Fit', '⭐⭐⭐⭐⭐ Ideal', '⭐⭐⭐ Good'],
                ]}
              />
              <div className="bg-white/5 p-6 my-4 border-l-2 border-gold">
                <p className="text-white/90">
                  <strong>Southeast Asia Recommendation:</strong> Catamarans dominate the charter market in Thailand, Indonesia, and the Philippines due to shallow draft access to hidden lagoons like Phang Nga Bay, Raja Ampat's coral reefs, and Palawan's secret beaches.
                </p>
              </div>
            </GuideAccordion>

            <GuideAccordion title="Yacht Terminology Essentials">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { term: 'Bow', def: 'Front of the yacht' },
                  { term: 'Stern', def: 'Rear of the yacht' },
                  { term: 'Port', def: 'Left side (when facing forward)' },
                  { term: 'Starboard', def: 'Right side' },
                  { term: 'Draft', def: 'Depth of yacht below waterline' },
                  { term: 'Beam', def: 'Width of yacht at widest point' },
                  { term: 'LOA', def: 'Length Overall (total length)' },
                  { term: 'Galley', def: 'Kitchen on a yacht' },
                  { term: 'Saloon', def: 'Main living area' },
                  { term: 'Flybridge', def: 'Upper deck with steering station' },
                  { term: 'Tender', def: 'Small boat for shore access' },
                  { term: 'Davit', def: 'Crane for lifting tender/water toys' },
                ].map((item, idx) => (
                  <div key={idx} className="bg-white/5 p-4">
                    <p className="text-gold font-semibold">{item.term}</p>
                    <p className="text-white/70 text-sm">{item.def}</p>
                  </div>
                ))}
              </div>
            </GuideAccordion>

            <GuideAccordion title="Quick Start: Your First Yacht Experience">
              <ol className="space-y-4">
                <li className="flex gap-4">
                  <span className="text-gold font-bold text-xl">1.</span>
                  <div>
                    <p className="font-semibold text-white">Choose Your Type</p>
                    <p className="text-white/70">Motor for speed/luxury, sailing for experience, catamaran for stability in Southeast Asia.</p>
                  </div>
                </li>
                <li className="flex gap-4">
                  <span className="text-gold font-bold text-xl">2.</span>
                  <div>
                    <p className="font-semibold text-white">Start with Day Charters</p>
                    <p className="text-white/70">Test the waters with 4-8 hour charters in Phuket or Langkawi before committing to weekly bookings.</p>
                  </div>
                </li>
                <li className="flex gap-4">
                  <span className="text-gold font-bold text-xl">3.</span>
                  <div>
                    <p className="font-semibold text-white">Understand Costs</p>
                    <p className="text-white/70">Base charter rate + APA (25-35%) for fuel/provisions + crew gratuity (10-20%).</p>
                  </div>
                </li>
                <li className="flex gap-4">
                  <span className="text-gold font-bold text-xl">4.</span>
                  <div>
                    <p className="font-semibold text-white">Book Through Experts</p>
                    <p className="text-white/70">Use platforms like ours that specialize in Southeast Asian waters and understand local permits/regulations.</p>
                  </div>
                </li>
              </ol>
            </GuideAccordion>
          </div>

          {/* Call to Action */}
          <div className="mt-12 glass-heavy p-10 text-center">
            <h3 className="text-3xl font-medium mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Ready to Explore Our Fleet?
            </h3>
            <p className="text-white/70 mb-6 max-w-2xl mx-auto">
              Now that you understand yacht basics, browse our curated collection of 20+ luxury yachts available for charter and sale across Southeast Asia.
            </p>
            <Link to="/listings">
              <Button className="bg-gold text-[#0A192F] hover:bg-[#E5C55D] px-10 py-6">
                View Yacht Listings
              </Button>
            </Link>
          </div>

          {/* Navigation */}
          <div className="mt-12 flex gap-4">
            <Link to="/guides/types-propulsion">
              <Button className="bg-gold text-[#0A192F] hover:bg-[#E5C55D]">
                Next: Types by Propulsion →
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
